import { useEffect, useState } from "react";
import { CalendarPlus } from "lucide-react";
import { previewAgendaIcs, readMailIcsText, type AgendaIcsPreview } from "@/lib/mail/importAgendaIcs";
import type { MailAttachment } from "@/lib/types/mail";

type Props = {
  accountId: number;
  folder: string;
  messageUid: number;
  file: MailAttachment;
  onAdd: () => void;
};

export function AgendaIcsBanner({ accountId, folder, messageUid, file, onAdd }: Props) {
  const [preview, setPreview] = useState<AgendaIcsPreview | null>(null);

  useEffect(() => {
    let cancelled = false;
    const attachment = file;
    setPreview(null);

    void (async () => {
      const text = await readMailIcsText({ accountId, folder, messageUid }, attachment);
      if (cancelled || !text.ok) return;
      const next = await previewAgendaIcs(text.ics);
      if (cancelled || !next.ok) return;
      setPreview(next.preview);
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, folder, messageUid, file.part, file.filename, file.size, file.mime]);

  const eventTitle = preview?.event.title?.trim() ?? "";
  const when = [preview?.event.whenLabel, preview?.event.location].filter(Boolean).join(" · ");
  const title = eventTitle || "Invitation calendrier";
  const meta = when || file.filename;

  return (
    <section className="mail-ics-banner" aria-label="Invitation calendrier">
      <div className="mail-ics-banner__icon" aria-hidden>
        <CalendarPlus />
      </div>
      <div className="mail-ics-banner__body">
        {eventTitle ? <p className="mail-ics-banner__kicker">Invitation calendrier</p> : null}
        <p className="mail-ics-banner__title">{title}</p>
        {meta ? <p className="mail-ics-banner__meta">{meta}</p> : null}
        {preview?.already ? (
          <p className="mail-ics-banner__status">
            Déjà dans « {preview.alreadyCalendarName || "votre agenda"} »
          </p>
        ) : null}
      </div>
      <button type="button" className="mail-ics-banner__btn" onClick={onAdd}>
        <CalendarPlus aria-hidden />
        Ajouter à mon agenda
      </button>
    </section>
  );
}
